import { Injectable } from "@angular/core";

const TOKEN_KEY = "smartbirds_access_token";
const ROLE_KEY = "smartbirds_role";
const PERMISSIONS_KEY = "smartbirds_permissions";
const USERNAME_KEY = "smartbirds_username";
const DISPLAY_NAME_KEY = "smartbirds_display_name";
const AVATAR_URL_KEY = "smartbirds_avatar_url";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly memoryStore = new Map<string, string>();

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return this.memoryStore.get(key) ?? null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      this.memoryStore.set(key, value);
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      this.memoryStore.delete(key);
    }
  }

  getToken(): string | null {
    return this.read(TOKEN_KEY);
  }

  setToken(token: string): void {
    this.write(TOKEN_KEY, token);
  }

  setSession(input: { token: string; role: string; permissions: string[]; username: string; displayName?: string; avatarUrl?: string }): void {
    this.write(TOKEN_KEY, input.token);
    this.write(ROLE_KEY, input.role);
    this.write(PERMISSIONS_KEY, JSON.stringify(input.permissions ?? []));
    this.write(USERNAME_KEY, input.username);
    if (input.displayName) this.write(DISPLAY_NAME_KEY, input.displayName);
    if (input.avatarUrl) this.write(AVATAR_URL_KEY, input.avatarUrl);
  }

  clearToken(): void {
    this.remove(TOKEN_KEY);
    this.remove(ROLE_KEY);
    this.remove(PERMISSIONS_KEY);
    this.remove(USERNAME_KEY);
    this.remove(DISPLAY_NAME_KEY);
    this.remove(AVATAR_URL_KEY);
  }

  isLoggedIn(): boolean {
    return Boolean(this.getToken());
  }

  getRole(): string {
    return this.read(ROLE_KEY) ?? "";
  }

  getPermissions(): string[] {
    try {
      return JSON.parse(this.read(PERMISSIONS_KEY) ?? "[]");
    } catch {
      return [];
    }
  }

  hasPermission(permission: string): boolean {
    return this.getPermissions().includes(permission);
  }

  getUsername(): string {
    return this.read(USERNAME_KEY) ?? "admin";
  }

  getDisplayName(): string {
    return this.read(DISPLAY_NAME_KEY) ?? "";
  }

  getAvatarUrl(): string {
    return this.read(AVATAR_URL_KEY) ?? "";
  }

  setProfile(displayName?: string, avatarUrl?: string): void {
    if (displayName) this.write(DISPLAY_NAME_KEY, displayName);
    if (avatarUrl) this.write(AVATAR_URL_KEY, avatarUrl);
  }
}
